import {
    addDays,
    differenceInCalendarDays,
    format,
    formatDuration,
    getDay,
    intervalToDuration,
    isDate,
    isSameDay,
    isSunday,
    isToday,
    isValid,
    lastDayOfWeek,
    parse,
    parseJSON,
    startOfMonth,
    startOfWeek,
    subDays,
} from "date-fns";
import enZA from "date-fns/locale/en-ZA";
import { Box, Button, Calendar, Grid, Text } from "grommet";
import * as React from "react";
import { CalendarViewModel } from "./CalendarStore";
import DailyAgendaView from "./DailyAgendaView";
import DayView from "./DayView";
import MonthlyAgendaView from "./MonthlyAgendaView";
import WeekView from "./WeekView";
import { first, take } from "lodash";
import _ from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ScrollContainer from "../components/ScrollContainer";
import CreateEventButton from "./CreateEventButton";

export interface CalendarViewEvent {
    id: string;
    title: string;
    body?: string;
    location?: string;
    duration: string;
    start: Date;
    dayOfEvent?: number;
    days: number;
    showDate: boolean;
    end: Date;
    color: string;
    allDay: boolean;
}

interface CalendarState {
    [key: string]: CalendarViewEvent[];
}

interface CalendarViewProps {
    calendars: CalendarViewModel[];
    onSelectDay;
    onReference;
    leftBarDay;
    month;
    viewMode;
    onChange;
}

const CalendarView = (CalendarViewProps: CalendarViewProps) => {
    const [selectedDay, setSelectedDay] = React.useState(new Date().toISOString());
    const [calendars, setCalendars] = React.useState(CalendarViewProps.calendars || ([] as CalendarViewModel[]));
    const [events, setEvents] = React.useState([] as CalendarViewEvent[]);
    const [calendarState, setCalendarState] = React.useState({} as CalendarState);
    const [maxRows, setMaxRows] = React.useState(0);

    const onSelect = (value) => {
        if (value == selectedDay) {
            setSelectedDay("");
        } else {
            setSelectedDay(value);
        }

        CalendarViewProps.onSelectDay && CalendarViewProps.onSelectDay(value);
    };

    React.useEffect(() => {
        getSelectedCalendarEvents();
    }, []);

    React.useEffect(() => {
        setSelectedDay(CalendarViewProps.leftBarDay || new Date().toISOString());
    }, [CalendarViewProps.leftBarDay]);

    React.useEffect(() => {
        getCalendarState();
    }, [events]);

    React.useEffect(() => {
        setCalendars(CalendarViewProps.calendars);
    }, [CalendarViewProps]);

    React.useEffect(() => {
        getSelectedCalendarEvents();
    }, [calendars]);

    const getSelectedCalendarEvents = () => {
        setEvents(
            calendars.reduce((agg, cal) => {
                if (cal.calendarEvents) {
                    agg = agg.concat(
                        cal.calendarEvents.map((e) => {
                            let start = parseJSON(e.startDate);
                            let end = parseJSON(e.endDate);

                            return {
                                id: e.id,
                                title: e.title,
                                body: e.body,
                                location: e.metadata?.location?.name,
                                duration: formatDuration(intervalToDuration({ end, start })),
                                days: differenceInCalendarDays(end, start),
                                showDate: false,
                                start,
                                dayOfEvent: 0,
                                end,
                                color: cal.color,
                                allDay: e.allDay,
                            } as CalendarViewEvent;
                        })
                    );
                }
                return agg;
            }, [] as CalendarViewEvent[])
        );
        return { events };
    };

    const getCalendarState = () => {
        let maxRows = 0;
        let cState = events
            .filter((event) => event.start >= new Date(2023, 1, 1))
            .reduce((agg, event) => {
                var startDate = format(event.start, "yyyy/MM/dd");
                let index = 0;

                if (agg[startDate]) {
                    agg[startDate].push(event);
                    index = agg[startDate].length - 1;
                } else {
                    agg[startDate] = [event];
                }

                if (!isSameDay(event.start, event.end)) {
                    let days = differenceInCalendarDays(event.end, event.start);
                    for (let i = 1; i <= days; i++) {
                        let currentDate = addDays(event.start, i);
                        let currentDateIndex = format(currentDate, "yyyy/MM/dd");
                        if (agg[currentDateIndex]) {
                            agg[currentDateIndex][index] = { ...event };
                        } else {
                            agg[currentDateIndex] = new Array(index + 1).fill(null);
                            agg[currentDateIndex][index] = { ...event };
                        }
                        if (!isSunday(currentDate) && !isSameDay(startOfMonth(currentDate), currentDate)) {
                            agg[currentDateIndex][index].title = "";
                        }
                    }
                }

                if (agg[startDate].length > maxRows) {
                    maxRows = startDate.length;
                }

                return agg;
            }, {});

        setCalendarState(cState);
        setMaxRows(maxRows);
    };

    const previousDay = () => {
        let day = subDays(new Date(selectedDay), 1);
        setSelectedDay(day.toISOString());
    };

    const nextDay = () => {
        let day = addDays(new Date(selectedDay), 1);
        setSelectedDay(day.toISOString());
    };

    const [lastDay, setLastDay] = React.useState(lastDayOfWeek(new Date(selectedDay)));
    const [firstDay, setFirstDay] = React.useState(subDays(lastDay, 6));

    React.useEffect(() => {
        let lastDay = lastDayOfWeek(new Date(selectedDay));
        setLastDay(lastDay);
        setFirstDay(subDays(lastDay, 6));
    }, [selectedDay]);

    const previousWeek = () => {
        setSelectedDay(subDays(new Date(selectedDay), 7).toISOString());
    };

    const nextWeek = () => {
        setSelectedDay(addDays(new Date(selectedDay), 7).toISOString());
    };

    return (
        <Box gap="small">
            {CalendarViewProps.viewMode == "month" && (
                <Grid columns={["75%", "25%"]} rows={["auto"]} areas={[["main", "rightBar"]]} pad={{ right: "medium" }} gap={{ row: "xsmall", column: "small" }}>
                    {/* main calendar */}
                    <Box gridArea="main" pad="xsmall">
                        <Calendar date={selectedDay} animate={false} showAdjacentDays={false} daysOfWeek fill range onReference={CalendarViewProps.onReference}>
                            {({ date, day, isSelected }) => {
                                let formattedDate = format(date, "yyyy/MM/dd");
                                const content = calendarState[formattedDate] || [];
                                return (
                                    <Box
                                        width="small"
                                        background={isSelected ? "background-front" : ""}
                                        height="small"
                                        onClick={() => onSelect(date.toISOString())}
                                        pad={isToday(date) == true ? {} : { top: "xxsmall" }}
                                        border={isToday(date) == true ? { side: "top", size: "medium", color: "brand" } : { side: "top", size: "xsmall" }}
                                    >
                                        <Box pad="xsmall">
                                            <Text size="large">{day}</Text>
                                        </Box>

                                        <Grid columns={{ count: 1, size: "auto" }} rows={new Array(4).fill("24px")} gap="xxsmall">
                                            {content.length > 0 &&
                                                _(content)
                                                    .orderBy(["start"], ["asc"])
                                                    .take(4)
                                                    .value()
                                                    .map((event, i) => (
                                                        <Box
                                                            pad={{ left: "xsmall" }}
                                                            direction="row"
                                                            key={formattedDate + "." + i}
                                                            justify="start"
                                                            background={event?.color}
                                                            style={{ position: "relative" }}
                                                        >
                                                            {event && <Text truncate="tip">{event.title}</Text>}
                                                            &nbsp;
                                                            {event && event.end > date && (
                                                                <div style={{ backgroundColor: event?.color, position: "absolute", left: "100%", width: "8%", height: "24px" }}>&nbsp;</div>
                                                            )}
                                                        </Box>
                                                    ))}
                                            {content.length > 4 && (
                                                <Box pad={{ left: "xsmall" }} direction="row" justify="start" background="background-back" style={{ position: "relative" }}>
                                                    <Text truncate="tip">
                                                        {content.length - 4} more event{content.length - 4 == 1 ? "" : "s"}
                                                    </Text>
                                                    &nbsp;
                                                </Box>
                                            )}
                                        </Grid>
                                    </Box>
                                );
                            }}
                        </Calendar>
                    </Box>

                    {/* agenda box at the right of the main calendar */}
                    <Box gridArea="rightBar" background="background-back" height="87vh" round="xsmall" pad="small">
                        <CreateEventButton calendars={calendars} onChange={() => CalendarViewProps.onChange()}></CreateEventButton>

                        {selectedDay ? (
                            <DailyAgendaView
                                selectedDay={new Date(selectedDay)}
                                events={events}
                                month={CalendarViewProps.month}
                                onNextDay={() => nextDay()}
                                onPrevDay={() => previousDay()}
                            ></DailyAgendaView>
                        ) : (
                            <MonthlyAgendaView events={events} month={CalendarViewProps.month}></MonthlyAgendaView>
                        )}
                    </Box>
                </Grid>
            )}

            {CalendarViewProps.viewMode == "week" && (
                <Box pad="small">
                    <Box direction="row" justify="between" align="center">
                        {firstDay && (
                            <Text size="xlarge" weight={"bold"}>
                                {format(firstDay, "MMMM") == format(lastDay, "MMMM")
                                    ? `${format(firstDay, "do")} - ${format(lastDay, "do MMMM yyyy")}`
                                    : `${format(firstDay, "do MMMM")} - ${format(lastDay, "do MMMM yyyy")}`}
                            </Text>
                        )}

                        <Box direction="row" gap="xxsmall">
                            <Button icon={<FontAwesomeIcon icon={["fad", "chevron-left"]} size="xl"></FontAwesomeIcon>} onClick={() => previousWeek()}></Button>
                            <Button icon={<FontAwesomeIcon icon={["fad", "chevron-right"]} size="xl"></FontAwesomeIcon>} onClick={() => nextWeek()}></Button>
                        </Box>
                    </Box>
                    <CreateEventButton calendars={calendars} onChange={() => CalendarViewProps.onChange()}></CreateEventButton>
                    <WeekView day={new Date(selectedDay)} events={events}></WeekView>
                </Box>
            )}

            {CalendarViewProps.viewMode == "day" && isValid(new Date(selectedDay)) && (
                <Box pad="small" height="87vh" gap={"xsmall"}>
                    <Box direction="row" justify="between" align="center">
                        <Text size="xlarge" weight={"bold"}>
                            {`${format(new Date(selectedDay), "do MMMM yyyy")}`}
                        </Text>
                        <Box direction="row" gap="xxsmall">
                            <Button icon={<FontAwesomeIcon icon={["fad", "chevron-left"]} size="xl"></FontAwesomeIcon>} onClick={() => previousDay()}></Button>
                            <Button icon={<FontAwesomeIcon icon={["fad", "chevron-right"]} size="xl"></FontAwesomeIcon>} onClick={() => nextDay()}></Button>
                        </Box>
                    </Box>
                    <CreateEventButton calendars={calendars} onChange={() => CalendarViewProps.onChange()}></CreateEventButton>
                    <ScrollContainer direction={"-y"}>
                        <DayView day={new Date(selectedDay)} events={events}></DayView>
                    </ScrollContainer>
                </Box>
            )}
        </Box>
    );
};

export default CalendarView;
